import { useState } from 'react';
import { ChevronRight } from 'lucide-react';

import { Spinner } from './Spinner';

export interface ToolOutputCardProps {
  summary: string;
  loading?: boolean;
  pinnable?: boolean;
  pinned?: boolean;
  error?: string;
  children?: React.ReactNode;
  onPinToggle?: () => void;
  className?: string;
  /** When set, renders compact inline card with tool name header + expandable IN/OUT sections */
  toolName?: string;
  /** Subtitle shown after tool name */
  description?: string;
  /** IN section content (tool input) */
  input?: string;
  /** OUT section content (tool output) */
  output?: string;
  /** Green dot for success, amber for in-progress */
  success?: boolean;
  /** Start expanded */
  defaultExpanded?: boolean;
  /** Full tool call ID (e.g. toolu_01SmK4QBcbMCEbPTXnwXy3Q4) */
  toolCallId?: string;
  /** Parent task tool call ID — tool was invoked by this subagent */
  parentToolUseId?: string;
  /** Human-readable subagent label e.g. "Subagent 2" */
  calledBy?: string;
  /** Subagent type for Task cards e.g. "general-purpose", "Bash" */
  subagentType?: string;
}

function StatusDot({ loading, success }: { loading: boolean; success: boolean }) {
  if (loading) return <Spinner size="sm" />;
  if (success) return <span className="w-1.5 h-1.5 rounded-full bg-sd-success shrink-0" aria-label="Success" />;
  return <span className="w-1.5 h-1.5 rounded-full bg-sd-warning shrink-0" aria-label="In progress" />;
}

/** Truncate a tool call ID to last N chars for compact display */
function shortId(id: string, chars = 8): string {
  return id.length > chars ? `…${id.slice(-chars)}` : id;
}

function truncateLines(text: string, maxLines: number): { text: string; truncated: boolean } {
  const lines = text.split('\n');
  if (lines.length <= maxLines) return { text, truncated: false };
  return { text: lines.slice(0, maxLines).join('\n'), truncated: true };
}

/** Replace file-system paths with just the basename (last segment) for compact display.
 *  Matches sequences like /foo/bar/baz.txt or /a/b/c/ and keeps only the last part.
 *  Preserves URLs (http:// and https://) intact. */
export function shortenPaths(text: string): string {
  // Temporarily replace URLs so path shortening doesn't mangle them
  const urls: string[] = [];
  const safe = text.replace(/https?:\/\/[^\s)"']+/g, (url) => {
    urls.push(url);
    return `\x00U${urls.length - 1}\x00`;
  });
  // Shorten absolute file-system paths (2+ segments starting with /)
  const shortened = safe.replace(/(\/[\w.@~+-]+){2,}(\/)?/g, (match) => {
    if (match.endsWith('/')) {
      const segments = match.replace(/\/+$/, '').split('/');
      return segments.at(-1)! + '/';
    }
    return match.split('/').at(-1)!;
  });
  // Restore URLs
  return shortened.replace(/\x00U(\d+)\x00/g, (_, i) => urls[Number(i)]!);
}

function InOutSection({ label, content, expanded }: { label: string; content: string; expanded: boolean }) {
  const shortened = shortenPaths(content);
  const { text: displayText, truncated } = expanded
    ? { text: shortened, truncated: false }
    : truncateLines(shortened, 3);
  return (
    <div className="flex gap-sd-2 min-w-0">
      <span className="text-[9px] font-semibold text-sd-text-muted/70 uppercase tracking-widest shrink-0 pt-0.5 w-6">{label}</span>
      <pre className="text-[11px] leading-[1.5] font-mono whitespace-pre-wrap break-words text-sd-text-secondary min-w-0 flex-1 m-0">{displayText}{truncated && <span className="text-sd-text-muted/50"> ...</span>}</pre>
    </div>
  );
}

/** Compact inline tool card — collapsed to a single quiet row, expandable on click */
function StructuredToolCard({
  toolName, description, input, output, loading, success, error,
  pinnable, pinned = false, onPinToggle, defaultExpanded,
  toolCallId, parentToolUseId, calledBy, subagentType,
  className = '',
}: ToolOutputCardProps & { toolName: string }) {
  const [expanded, setExpanded] = useState(defaultExpanded ?? false);
  const hasContent = !!(input || output);
  const isExpandable = !loading && hasContent;

  const toggle = () => {
    if (isExpandable) setExpanded((prev) => !prev);
  };

  const interactiveProps = isExpandable
    ? {
        role: 'button' as const,
        'aria-expanded': expanded,
        'aria-label': `${toolName}${description ? ` ${description}` : ''}`,
        tabIndex: 0,
        onClick: toggle,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        },
      }
    : {};

  return (
    <div
      {...interactiveProps}
      data-testid="tool-output-card"
      className={`group/tool rounded-sd-sm border-l-2 ${error ? 'border-l-sd-error/40 bg-sd-error/5' : expanded ? 'border-l-sd-primary/30 bg-sd-surface-elevated/50' : 'border-l-sd-border/40 bg-transparent'} w-full${isExpandable ? ' cursor-pointer hover:bg-sd-surface-elevated/40 transition-all duration-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sd-primary/50 focus-visible:ring-offset-0' : ''} ${className}`.trim()}
    >
      {/* Header row — single line, left-to-right information hierarchy:
          [chevron] [dot] TOOLNAME  id  ·  description  ·  via parent-id  [subagent-type badge] */}
      <div className="flex items-center gap-1.5 px-2 py-1 min-w-0">
        {isExpandable && (
          <ChevronRight
            size={11}
            className={`shrink-0 text-sd-text-muted/40 transition-transform duration-100 ${expanded ? 'rotate-90' : ''}`}
            aria-hidden="true"
          />
        )}
        <StatusDot loading={!!loading} success={!!success} />

        {/* PRIMARY: tool name */}
        <span className="text-[11px] font-semibold text-sd-text-muted tracking-wide uppercase shrink-0">{toolName}</span>

        {/* PRIMARY: own short ID — always shown, right after name */}
        {toolCallId && (
          <span
            className="text-[9px] font-mono text-sd-text-muted/55 tracking-tight shrink-0 leading-none"
            title={toolCallId}
          >
            {shortId(toolCallId)}
          </span>
        )}

        {/* SEPARATOR */}
        {(description || (calledBy && parentToolUseId)) && (
          <span className="text-sd-text-muted/25 shrink-0 text-[10px]">·</span>
        )}

        {/* SECONDARY: description — truncated, takes remaining space */}
        {description && (
          <span className="text-[11px] text-sd-text-muted/65 truncate min-w-0">{description}</span>
        )}

        {/* TERTIARY: via attribution — inline, not far-right */}
        {calledBy && parentToolUseId && (
          <>
            {description && <span className="text-sd-text-muted/25 shrink-0 text-[10px]">·</span>}
            <span
              className="text-[9.5px] text-sd-text-muted/55 shrink-0 whitespace-nowrap leading-none"
              title={`Called by ${calledBy} (${parentToolUseId})`}
            >
              via <span className="font-mono">{shortId(parentToolUseId)}</span>
            </span>
          </>
        )}

        {pinnable && (
          <button
            type="button"
            aria-label={pinned ? 'Unpin' : 'Pin'}
            className="ml-auto shrink-0 p-0 bg-transparent border-none cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sd-primary/50 rounded-sd-sm"
            onClick={(e) => { e.stopPropagation(); onPinToggle?.(); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onPinToggle?.();
              }
            }}
          >
            <svg className={`w-3 h-3 shrink-0 transition-colors duration-100 ${pinned ? 'text-sd-warning' : 'text-sd-text-muted/30 hover:text-sd-warning/60'}`} viewBox="0 0 16 16" fill={pinned ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5.5 2.5L10.5 2.5L10.5 6.5L12 8V9.5H8.5V13.5L8 14.5L7.5 13.5V9.5H4V8L5.5 6.5V2.5Z" />
            </svg>
          </button>
        )}
      </div>

      {/* Error message */}
      {error && (
        <p className="text-[11px] text-sd-error px-2 pb-1 mb-0">{error}</p>
      )}

      {/* IN/OUT content area — hidden by default, revealed on expand */}
      {hasContent && (
        <div className={`overflow-hidden transition-[max-height,opacity] duration-100 ease-in-out motion-reduce:transition-none ${expanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="mx-2 mb-1.5 bg-sd-surface/40 border border-sd-border/10 rounded-sm px-2 py-1.5 flex flex-col gap-0.5">
            {subagentType && (
              <div className="flex gap-sd-2 min-w-0 pb-0.5 mb-0.5 border-b border-sd-border/10">
                <span className="text-[9px] font-semibold text-sd-text-muted/70 uppercase tracking-widest shrink-0 pt-0.5 w-6">TYPE</span>
                <span className="text-[11px] text-sd-text-muted/60 font-mono">{subagentType}</span>
              </div>
            )}
            {input && <InOutSection label="IN" content={input} expanded={expanded} />}
            {output && <InOutSection label="OUT" content={output} expanded={expanded} />}
          </div>
        </div>
      )}

      {/* Loading placeholder — minimal inline */}
      {loading && !hasContent && (
        <div className="px-2 pb-1">
          <span className="text-[10px] text-sd-text-muted/50 tracking-wide">Running...</span>
        </div>
      )}
    </div>
  );
}

/** Generic tool output card (legacy). Renders summary-only flat card. */
function LegacyToolCard({ summary, loading, pinnable, pinned = false, error, children, onPinToggle, className = '' }: ToolOutputCardProps) {
  const [expanded, setExpanded] = useState(false);
  const isExpandable = !loading && !!children;

  const toggle = () => {
    if (isExpandable) setExpanded((prev) => !prev);
  };

  const interactiveProps = isExpandable
    ? {
        role: 'button' as const,
        'aria-expanded': expanded,
        'aria-label': summary,
        tabIndex: 0,
        onClick: toggle,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        },
      }
    : {};

  return (
    <div
      {...interactiveProps}
      className={`p-sd-2 rounded-sd-md ${error ? 'bg-sd-error/10' : 'bg-sd-surface-elevated'} shadow-[0_1px_2px_rgba(0,0,0,0.04)] w-full${isExpandable ? ' cursor-pointer hover:bg-sd-surface-elevated/80 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-primary focus-visible:ring-offset-1' : ''} ${className}`.trim()}
    >
      <div className="flex items-center gap-sd-1">
        {loading ? (
          <Spinner size="sm" />
        ) : (
          <svg className="w-3.5 h-3.5 text-sd-text-muted opacity-60 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9.5 2.5L13.5 6.5L6.5 13.5H2.5V9.5L9.5 2.5Z" />
            <path d="M8 4L12 8" />
          </svg>
        )}
        <span className={`text-sd-sm font-medium ${error ? 'text-sd-error' : 'text-sd-text-secondary'}`}>{error || summary}</span>
        {pinnable && (
          <button
            type="button"
            aria-label={pinned ? 'Unpin' : 'Pin'}
            className="shrink-0 p-0 bg-transparent border-none cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-primary focus-visible:ring-offset-1 rounded-sd-sm"
            onClick={(e) => { e.stopPropagation(); onPinToggle?.(); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onPinToggle?.();
              }
            }}
          >
            <svg className={`w-4 h-4 shrink-0 transition-colors duration-150 ${pinned ? 'text-sd-warning' : 'text-sd-text-muted hover:text-sd-warning/70'}`} viewBox="0 0 16 16" fill={pinned ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5.5 2.5L10.5 2.5L10.5 6.5L12 8V9.5H8.5V13.5L8 14.5L7.5 13.5V9.5H4V8L5.5 6.5V2.5Z" />
            </svg>
          </button>
        )}
      </div>
      {isExpandable && (
        <div className={`overflow-hidden transition-[max-height,opacity] duration-150 ease-in-out motion-reduce:transition-none ${expanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="text-sd-sm text-sd-text mt-sd-2 bg-sd-surface/50 border border-sd-border/20 rounded-sd-sm p-sd-2">{children}</div>
        </div>
      )}
    </div>
  );
}

export function ToolOutputCard(props: ToolOutputCardProps) {
  if (props.toolName) {
    return <StructuredToolCard {...props} toolName={props.toolName} />;
  }
  return <LegacyToolCard {...props} />;
}
